import React, { useEffect, useState } from 'react';
import { EnvStatusTracker, EnvStatus } from '../style';
import {
  CLOUD_SERVICE_PAGE_CONSTANTS,
} from "utility/constants";
import { isRoleAdmin } from "pages/mlops/ducks/utils";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "api-store/store";
import {
  updateWandbProvisioningService,
} from "services/wandbService";
import { CircularProgress } from "@mui/material";
import { callToGetRequestDetails } from "../ducks/slice";

interface EnvData {
  status?: string;
  message?: string;
  updated_at?: string;
  [key: string]: any;
}

interface RequestItemMetaData {
  updated_at?: string;
  status?: string;
  [envKey: string]: EnvData | string | undefined;
}

interface RequestItem {
  request_item: string;
  meta_data?: RequestItemMetaData;
}

interface RequestDetail {
  request_stage: string;
  [key: string]: any;
}

interface RequestNode {
  success: React.ReactNode;
  failed: React.ReactNode;
  pending: React.ReactNode;
  default: React.ReactNode;
}

interface EnvStatusTrackerProps {
  requestItem: RequestItem;
  requestDetail: RequestDetail;
  successStatus: string[];
  requestNodes: RequestNode;
  request_number:string;
  req_ambassador_email:string;
}

const StageEnvStatusTracker: React.FC<EnvStatusTrackerProps> = ({
  requestItem,
  requestDetail,
  successStatus,
  requestNodes,
  request_number,
  req_ambassador_email
}) => {
  const accessToken = useSelector((rootState: RootState) => rootState.userData.access_token);
  const isAdmin = isRoleAdmin(accessToken);
  const [loadingStates, setLoadingStates] = useState<{[key: string]: boolean}>({});
  const dispatch = useDispatch();
  
  useEffect(()=>{
    setLoadingStates({})
  },[requestItem])

  if (
    (
      requestItem.request_item !== "role_policy_creation" && 
      requestItem.request_item !== "cloud_request_execution" &&
      requestItem.request_item !== "wandb_onboarding" &&
      requestItem.request_item !== "sagemaker_provisioning"
    ) || !requestItem?.meta_data
  ) { return null; }
  
  const handleMarkProvisioned = async (envKey: string) => {
    setLoadingStates(prev => ({ ...prev, [envKey]: true }));    
    try {
      await updateWandbProvisioningService({
        request_number: request_number,
        status: "success",
        env: envKey,
        user_email: req_ambassador_email,
        message: ""
      });
    } catch (error) {
      console.error("Provisioning failed:", error);
    } finally {
      dispatch(callToGetRequestDetails({ requestId: request_number, isSilentRefresh: true, isRefreshStatusFlow: false }))
    }
  }

  const renderWandbProvisioningAction = (envKey: string, envData: EnvData): React.ReactNode => {
    if (requestItem.request_item === "wandb_onboarding" && isAdmin) {
      const isLoading = loadingStates[envKey] || false;
      return (
        <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
          {isLoading ? (
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size="0.7rem" color="inherit" />
            </span>
          ) : (
            <span 
              style={envData?.status?.toLowerCase() !== "success" ? 
                { cursor: 'pointer', textDecoration: 'underline' } : 
                {}
              }
              onClick={envData?.status?.toLowerCase() !== "success" ? 
                () => handleMarkProvisioned(envKey) : 
                undefined
              }
            >
              {envData?.status?.toLowerCase() !== "success" ? CLOUD_SERVICE_PAGE_CONSTANTS.markedProvisioning : CLOUD_SERVICE_PAGE_CONSTANTS.provisionedSuccessfully}
            </span>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <EnvStatusTracker>
      {Object.keys(requestItem.meta_data)
        .filter(key => key !== 'updated_at' && key !== 'status')
        .map((envKey, envIndex) => {
          const envData = requestItem.meta_data![envKey] as EnvData;
          const envDisplayName = envKey.toUpperCase(); 
          
          return (
            <div key={envIndex}>
              <EnvStatus>
                <div style={{ marginRight: '12px' }}>
                  {(() => {
                    const status = envData?.status?.toLowerCase() || '';
                    
                    if (successStatus.includes(status)) {
                      return requestNodes["success"];
                    }
                    
                    if (status === "failed") {
                      return requestNodes["failed"];
                    }
                    
                    if (requestDetail.request_stage === requestItem.request_item) {
                      return requestNodes["pending"];
                    }
                    
                    return requestNodes["default"];
                  })()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {envDisplayName}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    {CLOUD_SERVICE_PAGE_CONSTANTS.statusTitles.status}: {envData?.status ? envData.status.charAt(0).toUpperCase() + envData.status.slice(1) : 'Pending'}
                  </div>
                  {envData?.message && (
                    <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                      {envData.message}
                    </div>
                  )}
                  {envData?.updated_at && (
                    <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>
                      {CLOUD_SERVICE_PAGE_CONSTANTS.statusTitles.updatedAt}: {new Date(envData.updated_at).toLocaleString()}
                    </div>
                  )}
                  {renderWandbProvisioningAction(envKey, envData)}
                </div>
              </EnvStatus>
            </div>
          );
        })}
    </EnvStatusTracker>
  );
};

export default StageEnvStatusTracker;